import { API_CONFIG } from '../config/apiConfig';

// ... (Asset, Liability, Income, etc. interfaces remain unchanged) ...

export interface ProfileRecord {
  id: string;
  name: string;
  relation: string;
  profileType: string;
  dob: string;
  gender: string;
  maritalStatus: string; // CORRECTED: Typo fixed
  country: string;
  state: string;
  city: string;
  currency: string;
  employmentStatus: string;
  retirementAge: number;
  profilePicture: string;
}


export interface DependentRecord {
  id: string;
  name: string;
  relation: string;
  profileType: string;
  dob: string;
  gender: string;
  maritalStatus: string;
  country: string;
  state: string;
  city: string;
  currency: string;
  employmentStatus: string;
  retirementAge: number;
}

// Define the API response structure for profile
export interface ProfileApiResponse {
  total: number;
  size: number;
  data: Array<{
    id?: string;
    name?: string;
    relation?: string;
    profile_type?: string;
    date_of_birth?: string; // Corrected to match transformation function
    gender?: string;
    marital_status?: string;
    country?: string;
    state?: string;
    city?: string;
    currency?: string;
    employment_status?: string;
    planned_retirement_age?: number; // Corrected to match transformation function
    profile_picture?: string;
  }>;
}

// Define the API response structure for dependents
export interface DependentsApiResponse {
  total: number;
  size: number;
  data: Array<{
    id?: string;
    name?: string;
    relation?: string;
    profile_type?: string;
    date_of_birth?: string; // Corrected to match transformation function
    gender?: string;
    marital_status?: string;
    country?: string;
    state?: string;
    city?: string;
    currency?: string;
    employment_status?: string;
    planned_retirement_age?: number; // Corrected to match transformation function
  }>;
}

// Define the delete response structure
interface DeleteApiResponse {
  date: string;
  code?: string;
  message: string;
}

interface SimpleDeleteApiResponse {
  message: string;
}

// Profile API payload interface
interface ProfileApiPayload {
  id?: string;
  name: string;
  relation: string;
  profile_type: string;
  date_of_birth: string;
  gender: string;
  marital_status: string; // CORRECTED: Typo fixed
  country: string;
  state: string;
  city: string;
  currency: string;
  employment_status: string;
  planned_retirement_age: number;
  profile_picture: string;
}

// Dependent API payload interface
interface DependentApiPayload {
  id?: string;
  name: string;
  relation: string;
  profile_type: string;
  date_of_birth: string;
  gender: string;
  marital_status: string; // CORRECTED: Typo fixed
  country: string;
  state: string;
  city: string;
  currency: string;
  employment_status: string;
  planned_retirement_age: number;
}

// --- KPI Data Interface ---
interface KpiData {
    totalAssets(totalAssets: any): string;
    totalLiabilities(totalLiabilities: any): string;
    totalIncome(totalIncome: any): string;
    totalExpenses(totalExpenses: any): string;
    totalGoals(totalGoals: any): string;
    totalInsurance(totalInsurance: any): string;
    total_asset_value: string;
    total_insurance: string;
    monthly_expense: string;
    financial_goal: string;
    monthly_income: string;
    total_liability: string;
}

interface KpiApiResponse {
  data: KpiData[];
  code: string;
  message: string;
}

const getAuthToken = (): string | null => {
  // On login, you should save the token like this: localStorage.setItem('authToken', token);
  const token = localStorage.getItem('authToken');
  if (!token) {
    console.error('Authentication Error: JWT Token not found in localStorage.');
    return null;
  }
  return token;
};

// Fetch profile data
export const fetchProfileData = async (): Promise<any | null> => {
  const token = getAuthToken();
  if (!token) {
    throw new Error('User not logged in. No auth token found.');
  }

  try {
    const response = await fetch(
      `${API_CONFIG.API_NODE_BASE_URL}/api/profile`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    // This part handles both 200 OK and 304 Not Modified correctly.
    // The browser provides the body from cache for a 304 response.
    if (response.status === 404) {
      console.log('Profile not found for this user.');
      return null;
    }

    if (!response.ok) {
      // This will catch errors like 500, 401, etc., but won't be triggered by 304.
      const errorBody = await response.json();
      throw new Error(errorBody.message || `HTTP error! status: ${response.status}`);
    }

    // --- THIS IS THE CRITICAL FIX ---
    // You must read the JSON body to get the data.
    const data = await response.json();
    console.log('Profile data fetched successfully.', data);
    return data; // Return the actual data

  } catch (error) {
    console.error('Error fetching profile data:', error);
    throw error;
  }
};

// Fetch dependents data
export const fetchDependentsData = async (): Promise<DependentsApiResponse> => {

    const token = getAuthToken();
  if (!token) {
    throw new Error('User not logged in. No auth token found.');
    
  }

  try {
    const response = await fetch(
      `${API_CONFIG.API_NODE_BASE_URL}/api/dependents`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: DependentsApiResponse = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching dependents data:', error);
    throw error;
  }
};

// Prepare profile payload for API
const prepareProfilePayload = (profile: ProfileRecord): ProfileApiPayload => {
  return {
    id: profile.id || undefined,
    name: profile.name,
    relation: profile.relation,
    profile_type: profile.profileType,
    date_of_birth: profile.dob,
    gender: profile.gender,
    marital_status: profile.maritalStatus, // CORRECTED: Typo fixed
    country: profile.country,
    state: profile.state,
    city: profile.city,
    currency: profile.currency,
    employment_status: profile.employmentStatus,
    planned_retirement_age: profile.retirementAge,
    profile_picture: profile.profilePicture
  };
};

// Prepare dependent payload for API
const prepareDependentPayload = (dependent: DependentRecord): DependentApiPayload => {
  return {
    id: dependent.id || undefined,
    name: dependent.name,
    relation: dependent.relation,
    profile_type: dependent.profileType,
    date_of_birth: dependent.dob,
    gender: dependent.gender,
   marital_status: dependent.maritalStatus, // CORRECTED: Typo fixed
    country: dependent.country,
    state: dependent.state,
    city: dependent.city,
    currency: dependent.currency,
    employment_status: dependent.employmentStatus,
    planned_retirement_age: dependent.retirementAge
  };
};

// Save or update profile record
export const saveOrUpdateProfileRecord = async (profile: ProfileRecord): Promise<any> => {
  const token = getAuthToken();
  if (!token) {
    throw new Error('User not logged in. No auth token found.');
  }
  try {
    const payload = prepareProfilePayload(profile);
    const response = await fetch(
      `${API_CONFIG.API_NODE_BASE_URL}/api/profile`,
      {
        method: 'POST', // <-- CORRECT METHOD
        headers: {
          'Content-Type': 'application/json', // <-- IMPORTANT for the body
          'Authorization': `Bearer ${token}`,  // <-- CRITICAL for authentication
        },
        body: JSON.stringify(payload), // 3. Send the transformed payload.
      }
    );
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log(`Profile record saved/updated successfully:`, result);
    return result;
    
  } catch (error) {
    console.error(`Error saving/updating profile record:`, error);
    throw error;
  }
};

// Save or update dependent record
export const saveOrUpdateDependentRecord = async (DependentsDataResponse: DependentRecord): Promise<any> => {
  const token = getAuthToken();
  try {
    const payload = prepareDependentPayload(DependentsDataResponse);
    
    const response = await fetch(
      `${API_CONFIG.API_NODE_BASE_URL}/api/dependents`,
      {
        
        method: 'POST', // <-- CORRECT METHOD
        headers: {
          'Content-Type': 'application/json', // <-- IMPORTANT for the body
          'Authorization': `Bearer ${token}`,  // <-- CRITICAL for authentication
        },
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log(`Dependent record saved/updated successfully:`, result);
    return result;
  } catch (error) {
    console.error(`Error saving/updating dependent record:`, error);
    throw error;
  }
};

// Delete dependent record
// export const deleteDependentRecord = async (id: string): Promise<{success: boolean; message: string}> => {
//   const token = getAuthToken();
//   try {
//     const response = await fetch(
//       `${API_CONFIG.API_NODE_BASE_URL}/api/dependents/${id}`,
//       {
//         method: 'DELETE',
//         headers: {
//           'Content-Type': 'application/json', // <-- IMPORTANT for the body
//           'Authorization': `Bearer ${token}`,  // <-- CRITICAL for authentication
//         },
//       //   body: JSON.stringify(payload),
//       }
      
//     );

//     if (!response.ok) {
//       const errorData = await response.json().catch(() => ({}));
//       throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
//     }

//     const result: DeleteApiResponse = await response.json();
    
//     if (result.code === '200' && result.message === 'Successful operation') {
//       return { success: true, message: 'Dependent record deleted successfully' };
//     } else {
//       throw new Error(result.message || 'Delete operation failed');
//     }
//   } catch (error: any) {
//     console.error('Error deleting dependent record:', error);
//     return { 
//       success: false, 
//       message: error.message || 'Failed to delete dependent record. Please try again.' 
//     };
//   }
// };

export const deleteDependentRecord = async (id: string): Promise<{ success: boolean; message: string }> => {
  const token = getAuthToken();
  if (!token) {
    // Immediately return a failure object if there's no token
    return { success: false, message: 'Authentication token not found.' };
  }

  try {
    const response = await fetch(
      `${API_CONFIG.API_NODE_BASE_URL}/api/dependents/${id}`,
      {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    // First, check if the HTTP request itself was successful (status 200-299)
    if (!response.ok) {
      // If not, try to parse an error message from the body, otherwise use the status text
      const errorData = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(errorData.message || `HTTP error! Status: ${response.status}`);
    }

    // If the request was successful, parse the JSON body
    const result: SimpleDeleteApiResponse = await response.json();
    
    // console.log("Delete response:", result);

    // Return a success object with the message from the backend
    return { success: true, message: result.message };

  } catch (error: any) {
    console.error('Error in deleteDependentRecord service:', error);
    // Return a failure object with the specific error message
    return { 
      success: false, 
      message: error.message || 'Failed to delete dependent record. Please try again.' 
    };
  }
};

// Fetch KPI data
export const fetchKpiData = async (): Promise<KpiApiResponse> => {
  try {
    const response = await fetch(
      `${API_CONFIG.BASE_URL}/list/dashboard_kpi_count`,
      {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'api_id': API_CONFIG.API_ID,
          'api_key': API_CONFIG.API_KEY,
          // 'Authorization': getAuthHeader(),
        }
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: KpiApiResponse = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching KPI data:', error);
    throw error;
  }
};


export const transformApiProfileToRecord = (apiData: any): ProfileRecord => ({
  id: apiData.id || '',
  name: apiData.name || '',
  relation: apiData.relation || '',
  profileType: apiData.profile_type || '',
  dob: apiData.date_of_birth || '',
  gender: apiData.gender || '',
  maritalStatus: apiData.marital_status || '', // CORRECTED
  country: apiData.country || '',
  state: apiData.state || '',
  city: apiData.city || '',
  currency: apiData.currency || '',
  employmentStatus: apiData.employment_status || '',
  retirementAge: apiData.planned_retirement_age || 0,
  profilePicture: apiData.profile_picture || ''
});

export const transformApiDependentToRecord = (apiData: any): DependentRecord => ({
  id: apiData.id || '',
  name: apiData.name || '',
  relation: apiData.relation || '',
  profileType: apiData.profile_type || '',
  dob: apiData.date_of_birth || '',
  gender: apiData.gender || '',
  maritalStatus: apiData.marital_status || '',
  country: apiData.country || '',
  state: apiData.state || '',
  city: apiData.city || '',
  currency: apiData.currency || '',
  employmentStatus: apiData.employment_status || '',
  retirementAge: apiData.planned_retirement_age || 0
});